import type {
  Child,
  ChildCreate,
  ChildTarget,
  ChildUpdate,
  CravingRequest,
  FavoriteCreate,
  IngredientUpdate,
  MealCreate,
  MealUpdate,
  MenuPhotoResponse,
  NestedMealParseResponse,
  PantryBulkCreate,
  PantryCreate,
  PantryParseResponse,
  PantryUpdate,
  RecipeRequest,
  RecipeSuggestionsResponse,
  ShoppingBulkCreate,
  ShoppingCreate,
  ShoppingUpdate,
  TargetsUpdate,
} from "@sytofood/shared";

import { apiClient } from "./api-client";
import { browserTzOffset } from "./day";

export type Lang = "pl" | "en" | "de";

// ADHD Home mode (ADR-0015): the user's reduced-density Home variant.
export type AdhdVariant = "A" | "B" | "C";

export type AuthUser = {
  id: string;
  email: string;
  name: string | null;
  emailVerified: boolean;
  activeHouseholdId: string | null;
  // Per-user referral code shown in TopBar and shareable as /r/<code>.
  // Null for legacy users until they call /auth/me/invite once.
  inviteCode: string | null;
  // UI + AI output language. Null for users predating migration 0009.
  preferredLanguage: Lang | null;
  // ADHD Home mode (ADR-0015). The server defaults legacy NULLs to
  // false / "A" so these are always concrete here.
  adhdEnabled: boolean;
  adhdVariant: AdhdVariant;
  // Feature flag (ADR-0014/0015): when false the UI hides the toggle and
  // PUT /auth/me/adhd responds 403.
  adhdModeAvailable: boolean;
};

export type TargetsView = {
  kcalTarget: number | null;
  proteinTargetG: number | null;
  fatTargetG: number | null;
  carbsTargetG: number | null;
  weightKg: number | null;
  acknowledgedLowTarget: boolean;
  biologicalSex: "male" | "female" | null;
  heightCm: number | null;
  birthYear: number | null;
  activityLevel: "sedentary" | "light" | "moderate" | "high" | "very-high" | null;
};

export type LoggedMeal = {
  id: string;
  name: string;
  kcal: number;
  proteinG: number | null;
  fatG: number | null;
  carbsG: number | null;
  portionGrams: number | null;
  confidence: number | null;
  mealType: "breakfast" | "lunch" | "dinner" | "snack" | null;
  source: string;
  hasIngredients: boolean;
  loggedAt: number;
};

// Dish totals returned after an ingredient edit/delete recalculation.
export type MealTotals = {
  kcal: number;
  proteinG: number;
  fatG: number;
  carbsG: number;
};

// An ingredient row as returned by GET /meals/:id/ingredients.
export type IngredientView = {
  id: string;
  mealId: string;
  name: string;
  portionGrams: number | null;
  kcal: number;
  proteinG: number | null;
  fatG: number | null;
  carbsG: number | null;
};

export type DailySummaryView = {
  day: string;
  meals: LoggedMeal[];
  totals: { kcal: number; proteinG: number; fatG: number; carbsG: number };
};

export type ParseMealView = NestedMealParseResponse & {
  rawInputText?: string;
  source?: "manual_ai" | "photo" | "voice" | "barcode";
};

export type FavoriteView = {
  id: string;
  name: string;
  kcal: number;
  proteinG: number | null;
  fatG: number | null;
  carbsG: number | null;
  portionGrams: number | null;
  useCount: number;
  lastUsedAt: number | null;
};

export type PantryItemView = {
  id: string;
  name: string;
  quantity: number | null;
  unit: string | null;
  category: string | null;
  isStaple: boolean;
  expiresAt: number | null;
  source: string;
  createdAt: number;
  updatedAt: number;
};

export type ParsedPantryView = PantryParseResponse & { source: "photo" };

export type ShoppingItemView = {
  id: string;
  name: string;
  quantity: number | null;
  unit: string | null;
  category: string | null;
  source: "manual" | "recipe" | "pantry-empty";
  completed: boolean;
  completedAt: number | null;
  createdAt: number;
  updatedAt: number;
};

type MeResponse = {
  user: AuthUser;
  memberships: { householdId: string; role: string; joinedAt: number }[];
};

type HouseholdMember = {
  userId: string;
  email: string;
  name: string | null;
  role: string;
  joinedAt: number;
};

type CurrentHouseholdResponse = {
  id: string;
  name: string;
  members: HouseholdMember[];
};

type InviteCreateResponse = {
  id: string;
  code: string;
  householdId: string;
  expiresAt: number;
};

type HouseholdCreateResponse = {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
};

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
  ) {
    super(message);
  }
}

async function ensureOk<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ message: res.statusText }))) as {
      message?: string;
      code?: string;
    };
    throw new ApiError(
      body.message ?? `Request failed: ${res.status}`,
      body.code ?? "ERROR",
      res.status,
    );
  }
  return res.json() as Promise<T>;
}

export const api = {
  signUp: async (input: {
    email: string;
    password: string;
    name?: string;
    inviteCode?: string;
    language?: Lang;
  }): Promise<{ user: AuthUser | null; kind: "created" | "pending" }> => {
    const res = await apiClient.auth["sign-up"].$post({ json: input });
    return ensureOk<{ user: AuthUser | null; kind: "created" | "pending" }>(res);
  },

  setLanguage: async (language: Lang): Promise<void> => {
    const res = await apiClient.auth.me.language.$put({ json: { language } });
    await ensureOk<{ language: Lang }>(res);
  },

  // Persist the ADHD Home preference (ADR-0015). Mirrors setLanguage:
  // callers fire-and-forget and update the auth context optimistically.
  setAdhd: async (enabled: boolean, variant: AdhdVariant): Promise<void> => {
    const res = await apiClient.auth.me.adhd.$put({ json: { enabled, variant } });
    await ensureOk<{ enabled: boolean; variant: AdhdVariant }>(res);
  },

  signupMode: async (): Promise<{ open: boolean }> => {
    const res = await apiClient.auth["signup-mode"].$get();
    return ensureOk<{ open: boolean }>(res);
  },

  adminPing: async (): Promise<{ admin: boolean }> => {
    try {
      const res = await apiClient.admin.ping.$get();
      if (!res.ok) return { admin: false };
      return (await res.json()) as { admin: boolean };
    } catch {
      return { admin: false };
    }
  },

  adminOverview: async () => {
    const res = await apiClient.admin.overview.$get();
    return ensureOk<{
      totalUsers: number;
      verifiedUsers: number;
      activeLast7d: number;
      totalAiCalls: number;
      totalCostCentsAllTime: number;
      thisMonthCostCents: number;
      costByModel: Array<{ model: string; calls: number; costCents: number }>;
      topSpendersThisMonth: Array<{ userId: string; email: string; costCents: number }>;
      costAlertsLast30d: number;
      totalReferrals: number;
      topReferrers: Array<{
        userId: string;
        email: string;
        inviteCode: string;
        inviteesCount: number;
      }>;
    }>(res);
  },

  adminListUsers: async () => {
    const res = await apiClient.admin.users.$get();
    return ensureOk<{
      users: Array<{
        id: string;
        email: string;
        name: string | null;
        emailVerifiedAt: number | null;
        createdAt: number;
        activeHouseholdId: string | null;
        inviteCode: string | null;
        invitedByCode: string | null;
        inviteesCount: number;
        totalAiCalls: number;
        totalCostCents: number;
        thisMonthCostCents: number;
        lastCallAt: number | null;
        mealCount: number;
      }>;
    }>(res);
  },

  adminUserDetail: async (userId: string) => {
    const res = await apiClient.admin.users[":id"].$get({ param: { id: userId } });
    return ensureOk<{
      id: string;
      email: string;
      name: string | null;
      inviteCode: string | null;
      invitedByCode: string | null;
      inviteesCount: number;
      totalAiCalls: number;
      totalCostCents: number;
      thisMonthCostCents: number;
      lastCallAt: number | null;
      mealCount: number;
      callsByModel: Array<{ model: string; calls: number; costCents: number }>;
      recentCalls: Array<{ at: number; endpoint: string; model: string; costCents: number }>;
    }>(res);
  },

  signIn: async (input: { email: string; password: string }): Promise<{ user: AuthUser }> => {
    const res = await apiClient.auth["sign-in"].$post({ json: input });
    return ensureOk<{ user: AuthUser }>(res);
  },

  signOut: async (): Promise<void> => {
    const res = await apiClient.auth["sign-out"].$post();
    await ensureOk<{ ok: boolean }>(res);
  },

  verifyEmail: async (code: string): Promise<void> => {
    const res = await apiClient.auth["verify-email"].$post({ json: { code } });
    await ensureOk<{ user: { id: string; emailVerified: true } }>(res);
  },

  resendVerification: async (): Promise<void> => {
    const res = await apiClient.auth["resend-verification"].$post();
    await ensureOk<{ ok: boolean }>(res);
  },

  requestPasswordReset: async (email: string): Promise<void> => {
    const res = await apiClient.auth["request-password-reset"].$post({ json: { email } });
    await ensureOk<{ ok: boolean }>(res);
  },

  confirmPasswordReset: async (code: string, password: string): Promise<void> => {
    const res = await apiClient.auth["confirm-password-reset"].$post({
      json: { code, password },
    });
    await ensureOk<{ ok: boolean }>(res);
  },

  getMe: async (): Promise<MeResponse> => {
    const res = await apiClient.auth.me.$get();
    return ensureOk<MeResponse>(res);
  },

  getMyInvite: async (): Promise<{ code: string; invites: number }> => {
    const res = await apiClient.auth.me.invite.$get();
    return ensureOk<{ code: string; invites: number }>(res);
  },

  // Fire-and-forget telemetry: how far the user corrected an AI estimate.
  // Never throws — a failed telemetry call must not affect the save flow.
  sendAiFeedback: async (input: {
    endpoint: "meal-photo" | "meal-voice" | "meal-parse" | "meal-barcode" | "menu-photo";
    name: string;
    aiKcal: number;
    finalKcal: number;
    aiPortionGrams: number | null;
    finalPortionGrams: number | null;
  }): Promise<void> => {
    try {
      await apiClient.me["ai-feedback"].$post({ json: input });
    } catch {
      // swallow — telemetry is best-effort
    }
  },

  createHousehold: async (input: { name: string }): Promise<HouseholdCreateResponse> => {
    const res = await apiClient.households.$post({ json: input });
    return ensureOk<HouseholdCreateResponse>(res);
  },

  getCurrentHousehold: async (householdId: string): Promise<CurrentHouseholdResponse> => {
    const res = await apiClient.households.current.$get(
      {},
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<CurrentHouseholdResponse>(res);
  },

  createInvite: async (householdId: string): Promise<InviteCreateResponse> => {
    const res = await apiClient.households.current.invites.$post(
      {},
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<InviteCreateResponse>(res);
  },

  acceptInvite: async (code: string): Promise<{ household: { id: string; name: string } }> => {
    const res = await apiClient.invites[":code"].accept.$post({
      param: { code: code.toUpperCase() },
    });
    return ensureOk<{ household: { id: string; name: string } }>(res);
  },

  getTargets: async (): Promise<TargetsView> => {
    const res = await apiClient.me.targets.$get();
    return ensureOk<TargetsView>(res);
  },

  putTargets: async (input: TargetsUpdate): Promise<TargetsView> => {
    const res = await apiClient.me.targets.$put({ json: input });
    return ensureOk<TargetsView>(res);
  },

  parseMeal: async (text: string): Promise<ParseMealView> => {
    const res = await apiClient.meals.parse.$post({ json: { text } });
    return ensureOk<ParseMealView>(res);
  },

  parseMealPhoto: async (
    imageDataUrl: string,
    childId?: string,
  ): Promise<ParseMealView> => {
    // childId switches the server-side prompt to child-portion mode
    // (smaller default portions, per-piece counting rule, filtered
    // recent-meals few-shot). Required when the active profile is a
    // child; omitting it costs ~3× over-estimation as a default-to-
    // adult-portion bug.
    const res = await apiClient.meals.photo.$post({
      json: childId ? { imageDataUrl, childId } : { imageDataUrl },
    });
    return ensureOk<ParseMealView>(res);
  },

  parseMealVoice: async (audioDataUrl: string): Promise<ParseMealView> => {
    const res = await apiClient.meals.voice.$post({ json: { audioDataUrl } });
    return ensureOk<ParseMealView>(res);
  },

  parseMealBarcode: async (ean: string, portionGrams?: number): Promise<ParseMealView> => {
    const res = await apiClient.meals.barcode.$post({
      json: portionGrams !== undefined ? { ean, portionGrams } : { ean },
    });
    return ensureOk<ParseMealView>(res);
  },

  saveMeals: async (input: MealCreate, householdId: string): Promise<{ meals: LoggedMeal[] }> => {
    const res = await apiClient.meals.$post(
      { json: input },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<{ meals: LoggedMeal[] }>(res);
  },

  getDailySummary: async (
    householdId: string,
    day?: string,
    opts?: { childId?: string },
  ): Promise<DailySummaryView> => {
    const tzOffset = String(browserTzOffset());
    // ADR-0018: when childId is provided, the server returns that child's
    // meals (meals.child_id = childId). When omitted, returns adult-owned
    // meals (child_id IS NULL) — legacy default, unchanged for users
    // without children.
    const base: { day?: string; tzOffset: string; childId?: string } = { tzOffset };
    if (day) base.day = day;
    if (opts?.childId) base.childId = opts.childId;
    const res = await apiClient.meals.$get(
      { query: base },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<DailySummaryView>(res);
  },

  // === Children (ADR-0018) ===
  getChildren: async (householdId: string): Promise<Child[]> => {
    const res = await apiClient.children.$get(
      {},
      { headers: { "x-household-id": householdId } },
    );
    const data = await ensureOk<{ children: Child[] }>(res);
    return data.children;
  },

  createChild: async (householdId: string, input: ChildCreate): Promise<Child> => {
    const res = await apiClient.children.$post(
      { json: input },
      { headers: { "x-household-id": householdId } },
    );
    const data = await ensureOk<{ child: Child }>(res);
    return data.child;
  },

  updateChild: async (
    householdId: string,
    id: string,
    patch: ChildUpdate,
  ): Promise<Child> => {
    const res = await apiClient.children[":id"].$patch(
      { param: { id }, json: patch },
      { headers: { "x-household-id": householdId } },
    );
    const data = await ensureOk<{ child: Child }>(res);
    return data.child;
  },

  deleteChild: async (householdId: string, id: string): Promise<void> => {
    const res = await apiClient.children[":id"].$delete(
      { param: { id } },
      { headers: { "x-household-id": householdId } },
    );
    await ensureOk<{ ok: true }>(res);
  },

  getChildTarget: async (householdId: string, childId: string): Promise<ChildTarget> => {
    const res = await apiClient.children[":id"].targets.$get(
      { param: { id: childId } },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<ChildTarget>(res);
  },

  // Nested-meal ingredients (ADR-0010).
  getMealIngredients: async (householdId: string, mealId: string): Promise<IngredientView[]> => {
    const res = await apiClient.meals[":id"].ingredients.$get(
      { param: { id: mealId } },
      { headers: { "x-household-id": householdId } },
    );
    const data = await ensureOk<{ ingredients: IngredientView[] }>(res);
    return data.ingredients;
  },

  updateIngredient: async (
    householdId: string,
    mealId: string,
    ingredientId: string,
    patch: IngredientUpdate,
  ): Promise<{ ingredient: IngredientView; meal: MealTotals | null }> => {
    const res = await apiClient.meals[":id"].ingredients[":ingredientId"].$patch(
      { param: { id: mealId, ingredientId }, json: patch },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<{ ingredient: IngredientView; meal: MealTotals | null }>(res);
  },

  deleteIngredient: async (
    householdId: string,
    mealId: string,
    ingredientId: string,
  ): Promise<{ meal: MealTotals | null }> => {
    const res = await apiClient.meals[":id"].ingredients[":ingredientId"].$delete(
      { param: { id: mealId, ingredientId } },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<{ meal: MealTotals | null }>(res);
  },

  updateMeal: async (
    householdId: string,
    mealId: string,
    patch: MealUpdate,
  ): Promise<LoggedMeal> => {
    const res = await apiClient.meals[":id"].$patch(
      { param: { id: mealId }, json: patch },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<LoggedMeal>(res);
  },

  deleteMeal: async (householdId: string, mealId: string): Promise<void> => {
    const res = await apiClient.meals[":id"].$delete(
      { param: { id: mealId } },
      { headers: { "x-household-id": householdId } },
    );
    await ensureOk<{ ok: boolean }>(res);
  },

  listFavorites: async (householdId: string): Promise<FavoriteView[]> => {
    const res = await apiClient.favorites.$get({}, { headers: { "x-household-id": householdId } });
    const data = await ensureOk<{ favorites: FavoriteView[] }>(res);
    return data.favorites;
  },

  createFavorite: async (householdId: string, input: FavoriteCreate): Promise<FavoriteView> => {
    const res = await apiClient.favorites.$post(
      { json: input },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<FavoriteView>(res);
  },

  logFavorite: async (householdId: string, favoriteId: string): Promise<LoggedMeal | null> => {
    const res = await apiClient.favorites[":id"].log.$post(
      { param: { id: favoriteId } },
      { headers: { "x-household-id": householdId } },
    );
    const data = await ensureOk<{ meal: LoggedMeal | null }>(res);
    return data.meal;
  },

  deleteFavorite: async (householdId: string, favoriteId: string): Promise<void> => {
    const res = await apiClient.favorites[":id"].$delete(
      { param: { id: favoriteId } },
      { headers: { "x-household-id": householdId } },
    );
    await ensureOk<{ ok: boolean }>(res);
  },

  listPantry: async (householdId: string): Promise<PantryItemView[]> => {
    const res = await apiClient.pantry.$get({}, { headers: { "x-household-id": householdId } });
    const data = await ensureOk<{ items: PantryItemView[] }>(res);
    return data.items;
  },

  createPantryItem: async (householdId: string, input: PantryCreate): Promise<PantryItemView> => {
    const res = await apiClient.pantry.$post(
      { json: input },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<PantryItemView>(res);
  },

  bulkCreatePantry: async (
    householdId: string,
    input: PantryBulkCreate,
  ): Promise<PantryItemView[]> => {
    const res = await apiClient.pantry.bulk.$post(
      { json: input },
      { headers: { "x-household-id": householdId } },
    );
    const data = await ensureOk<{ items: PantryItemView[] }>(res);
    return data.items;
  },

  parsePantryPhoto: async (
    householdId: string,
    imageDataUrl: string,
  ): Promise<ParsedPantryView> => {
    const res = await apiClient.pantry.photo.$post(
      { json: { imageDataUrl } },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<ParsedPantryView>(res);
  },

  updatePantryItem: async (
    householdId: string,
    id: string,
    patch: PantryUpdate,
  ): Promise<PantryItemView> => {
    const res = await apiClient.pantry[":id"].$patch(
      { param: { id }, json: patch },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<PantryItemView>(res);
  },

  deletePantryItem: async (householdId: string, id: string): Promise<void> => {
    const res = await apiClient.pantry[":id"].$delete(
      { param: { id } },
      { headers: { "x-household-id": householdId } },
    );
    await ensureOk<{ ok: boolean }>(res);
  },

  suggestRecipes: async (
    householdId: string,
    request: RecipeRequest = {},
  ): Promise<RecipeSuggestionsResponse> => {
    const res = await apiClient.pantry.recipes.$post(
      { json: request },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<RecipeSuggestionsResponse>(res);
  },

  suggestForCraving: async (
    householdId: string,
    request: CravingRequest,
  ): Promise<RecipeSuggestionsResponse> => {
    const res = await apiClient.pantry.craving.$post(
      { json: { ...request, tzOffset: browserTzOffset() } },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<RecipeSuggestionsResponse>(res);
  },

  parseMenuPhoto: async (
    householdId: string,
    input: { imageDataUrls: string[]; targetLanguage: string },
  ): Promise<MenuPhotoResponse> => {
    const res = await apiClient.menu.photo.$post(
      { json: input },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<MenuPhotoResponse>(res);
  },

  exportMyData: async (): Promise<void> => {
    // Returns a streamed JSON download. Trigger native browser download.
    const res = await apiClient.me.export.$get();
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      throw new Error(body.message ?? `Request failed: ${res.status}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const filename = `sytofood-export-${new Date().toISOString().slice(0, 10)}.json`;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  deleteMyAccount: async (): Promise<void> => {
    const res = await apiClient.me.account.$delete();
    await ensureOk<{ ok: boolean }>(res);
  },

  listShopping: async (householdId: string): Promise<ShoppingItemView[]> => {
    const res = await apiClient.shopping.$get({}, { headers: { "x-household-id": householdId } });
    const data = await ensureOk<{ items: ShoppingItemView[] }>(res);
    return data.items;
  },

  createShoppingItem: async (
    householdId: string,
    input: ShoppingCreate,
  ): Promise<ShoppingItemView> => {
    const res = await apiClient.shopping.$post(
      { json: input },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<ShoppingItemView>(res);
  },

  bulkCreateShopping: async (
    householdId: string,
    input: ShoppingBulkCreate,
  ): Promise<ShoppingItemView[]> => {
    const res = await apiClient.shopping.bulk.$post(
      { json: input },
      { headers: { "x-household-id": householdId } },
    );
    const data = await ensureOk<{ items: ShoppingItemView[] }>(res);
    return data.items;
  },

  updateShoppingItem: async (
    householdId: string,
    id: string,
    patch: ShoppingUpdate,
  ): Promise<ShoppingItemView> => {
    const res = await apiClient.shopping[":id"].$patch(
      { param: { id }, json: patch },
      { headers: { "x-household-id": householdId } },
    );
    return ensureOk<ShoppingItemView>(res);
  },

  deleteShoppingItem: async (householdId: string, id: string): Promise<void> => {
    const res = await apiClient.shopping[":id"].$delete(
      { param: { id } },
      { headers: { "x-household-id": householdId } },
    );
    await ensureOk<{ ok: boolean }>(res);
  },

  clearCompletedShopping: async (householdId: string): Promise<number> => {
    const res = await apiClient.shopping.completed.$delete(
      {},
      { headers: { "x-household-id": householdId } },
    );
    const data = await ensureOk<{ removed: number }>(res);
    return data.removed;
  },
};
